import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Home as HomeIcon, Building2, Phone, Mail, Upload, Loader2, CheckCircle2, X, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Air Conditioning Installation",
  "Air Conditioning Repair",
  "Air Conditioning Service",
  "Ducted Air Conditioning",
  "Heating",
  "Commercial HVAC",
  "Not Sure / Need Advice",
];

const Quote = () => {
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [d, setD] = useState({
    name: "", phone: "", email: "", property_type: "Residential",
    service: "", preferred_contact: "Phone", message: "",
  });

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  const uploadPhotos = async (files) => {
    setUploading(true);
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd);
        setPhotos((p) => [...p, { id: data.id, name: file.name, url: URL.createObjectURL(file) }]);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!d.name.trim() || !d.phone.trim() || !d.email.trim() || !d.message.trim()) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { ...d, photo_ids: photos.map((p) => p.id) });
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  const inp =
    "w-full h-14 px-5 rounded-2xl bg-white border border-black/10 focus:border-js-blue focus:ring-2 focus:ring-js-blue/20 outline-none transition-colors duration-200";
  const label = "block text-sm font-semibold text-js-charcoal mb-2";

  if (done)
    return (
      <div className="min-h-screen grid place-items-center bg-js-grey pt-[72px] px-5" data-testid="quote-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-lg bg-white rounded-3xl p-12 border border-black/5 shadow-[0_20px_60px_rgba(0,0,0,0.08)]"
        >
          <span className="grid place-items-center w-20 h-20 rounded-full bg-js-blue/10 text-js-blue mx-auto mb-6">
            <CheckCircle2 size={44} />
          </span>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mb-4">Thank you!</h1>
          <p className="text-js-charcoal/60 leading-relaxed">
            Your quote request has been received. The J&amp;S Air Conditioning team will review your enquiry
            and get back to you shortly.
          </p>
          <Link
            to="/"
            data-testid="quote-back-home"
            className="inline-block mt-8 px-7 py-3.5 rounded-full bg-js-blue text-white font-semibold hover:bg-js-dark transition-colors duration-300"
          >
            Back to Home
          </Link>
        </motion.div>
      </div>
    );

  return (
    <div className="min-h-screen bg-js-grey pt-[72px]" data-testid="quote-page">
      <div className="max-w-2xl mx-auto px-5 lg:px-8 py-16">
        <div className="text-center mb-10">
          <span className="text-js-blue font-semibold text-sm tracking-wide">FREE QUOTE</span>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tighter mt-3">Request your free quote</h1>
          <p className="text-js-charcoal/60 mt-4 leading-relaxed">
            Tell us a little about your project and the J&amp;S Air Conditioning team will get back to you shortly.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="quote-form"
          className="bg-white rounded-3xl p-8 md:p-10 border border-black/5 shadow-[0_12px_40px_rgba(0,0,0,0.05)] space-y-7"
        >
          {/* Property type */}
          <div>
            <span className={label}>Property Type</span>
            <div className="grid grid-cols-2 gap-3">
              {[{ v: "Residential", icon: HomeIcon }, { v: "Commercial", icon: Building2 }].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`property-${o.v.toLowerCase()}`}
                  onClick={() => set("property_type", o.v)}
                  className={`p-5 rounded-2xl border-2 flex flex-col items-center gap-2 transition-colors duration-300 ${
                    d.property_type === o.v ? "border-js-blue bg-js-blue/5" : "border-black/10 hover:border-js-blue/40"
                  }`}
                >
                  <o.icon size={26} className={d.property_type === o.v ? "text-js-blue" : "text-js-charcoal/40"} />
                  <span className="font-semibold">{o.v}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className={label} htmlFor="q-name">Full Name <span className="text-js-red">*</span></label>
            <input id="q-name" data-testid="quote-name" className={inp} value={d.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" required />
          </div>

          {/* Phone + Email */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={label} htmlFor="q-phone">Phone Number <span className="text-js-red">*</span></label>
              <input id="q-phone" type="tel" data-testid="quote-phone" className={inp} value={d.phone} onChange={(e) => set("phone", e.target.value)} placeholder="e.g. 0400 000 000" required />
            </div>
            <div>
              <label className={label} htmlFor="q-email">Email Address <span className="text-js-red">*</span></label>
              <input id="q-email" type="email" data-testid="quote-email" className={inp} value={d.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" required />
            </div>
          </div>

          {/* Service required */}
          <div>
            <label className={label} htmlFor="q-service">Service Required</label>
            <select id="q-service" data-testid="quote-service" className={`${inp} appearance-none cursor-pointer`} value={d.service} onChange={(e) => set("service", e.target.value)}>
              <option value="">Select a service…</option>
              {SERVICE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Preferred contact method */}
          <div>
            <span className={label}>Preferred Contact Method</span>
            <div className="grid grid-cols-2 gap-3">
              {[{ v: "Phone", icon: Phone }, { v: "Email", icon: Mail }].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`contact-${o.v.toLowerCase()}`}
                  onClick={() => set("preferred_contact", o.v)}
                  className={`p-4 rounded-2xl border-2 flex items-center justify-center gap-2 font-semibold transition-colors duration-300 ${
                    d.preferred_contact === o.v ? "border-js-blue bg-js-blue/5 text-js-blue" : "border-black/10 hover:border-js-blue/40"
                  }`}
                >
                  <o.icon size={18} /> {o.v}
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className={label} htmlFor="q-message">Message / Project Details <span className="text-js-red">*</span></label>
            <textarea
              id="q-message"
              rows={6}
              data-testid="quote-message"
              className="w-full p-5 rounded-2xl bg-white border border-black/10 focus:border-js-blue focus:ring-2 focus:ring-js-blue/20 outline-none transition-colors duration-200"
              value={d.message}
              onChange={(e) => set("message", e.target.value)}
              placeholder="Tell us about your space, existing system, and what you need…"
              required
            />
          </div>

          {/* Photos */}
          <div>
            <span className={label}>Upload Photos <span className="text-js-charcoal/40 font-normal">(optional)</span></span>
            <label data-testid="quote-photo-upload" className="flex flex-col items-center justify-center gap-2 h-36 rounded-2xl border-2 border-dashed border-black/20 cursor-pointer hover:border-js-blue transition-colors duration-300">
              {uploading ? <Loader2 size={26} className="animate-spin text-js-blue" /> : <Upload size={26} className="text-js-blue" />}
              <span className="text-sm text-js-charcoal/60">Click to upload photos</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(e.target.files)} />
            </label>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
                {photos.map((p, i) => (
                  <div key={i} className="relative rounded-xl overflow-hidden aspect-square group">
                    <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setPhotos((ps) => ps.filter((_, k) => k !== i))} className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/60 text-white">
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            data-testid="quote-submit"
            disabled={submitting}
            className="group w-full flex items-center justify-center gap-2 px-8 py-4.5 py-4 rounded-full bg-js-red text-white font-bold text-lg hover:bg-js-red/90 transition-colors duration-300 disabled:opacity-60"
          >
            {submitting && <Loader2 size={20} className="animate-spin" />}
            GET MY FREE QUOTE
            {!submitting && <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Quote;
